import { useNavigate, useSearchParams } from "react-router-dom";

import { priceFormatter } from "~/utils/price-formatter";
import { T } from "@repo/transifex";
import { useUserProductsPos } from "~/api/products/hooks/use-user-products";
import { Button, Dialog, DialogPayment } from "~/components";
import { SwishIcon } from "~/components/SwishIcon";
import { useOrder } from "~/hooks/queries/orders";
import {
  useAbortTransaction,
  useTransactionStatus
} from "~/hooks/queries/terminals";
import { useOpenCashDrawer } from "~/hooks";
import { calculateAmountForSaldo } from "~/utils/basket";
import { useSplitPaymentFlow } from "~/hooks/use-split-payment-flow";
import { useUserBalance } from "~/hooks/use-user-balance";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import { useCurrentTillLocalisation } from "~/api/tills/hooks/use-till-current/localisation";
import { useBasketContext } from "~/providers/store/basket/context";

const PosSwishPage = () => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId");
  const serviceId = searchParams.get("serviceId");
  const shouldPoll = searchParams.get("poll");

  const openCashDrawer = useOpenCashDrawer();

  const { data: userProducts } = useUserProductsPos();

  const { data: order, status } = useOrder(orderId);

  const { data: till } = useCurrentTill();
  const { locale, currency } = useCurrentTillLocalisation();
  const terminalId = till?.terminalId ?? undefined;

  const { mutate: abortTransaction, status: abortTransactionStatus } =
    useAbortTransaction();

  useTransactionStatus(
    { serviceId, orderId, terminalId },
    shouldPoll === "true"
  );

  const { isActive: isSplitPaymentActive, unitReceived } =
    useSplitPaymentFlow();

  const unitPriceTotalInclVat = useBasketContext(
    (state) => state.sum.unitPriceTotalInclVat
  );

  const availableBalance = useUserBalance({
    unitAvailableInclVat:
      userProducts?.user?.collectLimit?.unitAvailableInclVat,
    balance: userProducts?.user?.balance
  });

  if (status === "error" || order?.status === "failed") {
    return <DialogPayment status="error" />;
  }

  if (order?.status === "cancelled") {
    return <DialogPayment status="cancelled" />;
  }

  if (order?.status === "paid") {
    return <DialogPayment orderId={order.id} status="success" />;
  }

  const totalPrice = calculateAmountForSaldo(
    unitPriceTotalInclVat,
    availableBalance
  );

  const totalPriceFormatted = priceFormatter({
    value: totalPrice.payWithOther - unitReceived,
    currency,
    locale
  });

  return (
    <Dialog className="max-w-sm" size="md">
      <div className="flex flex-col items-center gap-y-5 p-5">
        <div className="flex w-full flex-col items-center gap-5 rounded-2xl bg-background-secondary p-5">
          <p className="text-lg font-bold text-text-secondary">
            <T _str="Awaiting payment" />
          </p>
          <p className="text-2xl font-bold">{totalPriceFormatted}</p>

          <div className="flex w-full items-center justify-center gap-2 rounded-2xl bg-orange-200 p-5">
            <SwishIcon />
            <p className="text-4xl font-bold text-orange-500">Swish</p>
          </div>
        </div>

        <Button
          disabled={!terminalId}
          loading={abortTransactionStatus === "pending"}
          onClick={() => {
            if (!terminalId) {
              throw new Error("Cannot abort transaction: missing terminalId");
            }
            abortTransaction(
              {
                ...(orderId && { orderId }),
                serviceId
              },
              {
                onSuccess: () => {
                  navigate("/pos");
                }
              }
            );

            if (isSplitPaymentActive) {
              openCashDrawer("automatic");
            }
          }}
          variant="danger"
        >
          <T _str="Cancel" />
        </Button>
      </div>
    </Dialog>
  );
};

export { PosSwishPage };
